import { useEffect, useMemo, useState } from 'react';
import { Flag, Languages, Loader2 } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Checkbox, Dialog, DialogContent, DialogFooter, Field as FormField, NativeSelect, Textarea, toast } from '@vit/ui';
import type { TranslationResult } from '@vit/contracts';
import { needsTranslation } from '@vit/domain';
import { languageName } from '@vit/i18n';
import { api, POLL_PENDING } from '../lib/api.js';
import { toastError } from '../lib/errors.js';
import { currentLocale, useTranslation } from '../lib/i18n.js';
import { MarkdownView } from './markdown.js';

type EntityKind = 'REQUEST' | 'WORK_ITEM' | 'COMMENT' | 'PROGRESS_ENTRY';
type Field = 'title' | 'descriptionMarkdown' | 'problemMarkdown' | 'currentBehavior' | 'expectedOutcome' | 'impact' | 'bodyMarkdown' | 'summaryMarkdown' | 'outcomeMarkdown' | 'acceptanceCriteria';

interface Pending {
  entityKind: EntityKind;
  entityId: string;
  fields: Set<Field>;
  resolvers: Array<(r: TranslationResult | null) => void>;
}

/**
 * Batches translation lookups from many components into one request per tick, so a long comment
 * thread costs one call against the translation rate limit.
 */
class TranslationBatcher {
  private queue = new Map<string, Pending>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  constructor(private readonly locale: string) {}

  load(entityKind: EntityKind, entityId: string, field: Field): Promise<TranslationResult | null> {
    return new Promise((resolve) => {
      const key = `${entityKind}:${entityId}`;
      const pending = this.queue.get(key) ?? { entityKind, entityId, fields: new Set<Field>(), resolvers: [] };
      pending.fields.add(field);
      pending.resolvers.push(resolve);
      this.queue.set(key, pending);
      this.timer ??= setTimeout(() => void this.flush(), 25);
    });
  }

  private async flush() {
    const batch = [...this.queue.values()].slice(0, 50);
    for (const p of batch) this.queue.delete(`${p.entityKind}:${p.entityId}`);
    this.timer = this.queue.size ? setTimeout(() => void this.flush(), 25) : null;
    try {
      const response = await api.call('requestTranslationsBatch', {
        body: { items: batch.map((p) => ({ entityKind: p.entityKind, entityId: p.entityId, fields: [...p.fields], targetLocale: this.locale as 'en' })) },
      });
      for (const p of batch) {
        const result = response!.data.find((r) => r.entityKind === p.entityKind && r.entityId === p.entityId) ?? null;
        p.resolvers.forEach((resolve) => resolve(result));
      }
    } catch {
      // Translation failures never block reading the original.
      for (const p of batch) p.resolvers.forEach((resolve) => resolve(null));
    }
  }
}

const batchers = new Map<string, TranslationBatcher>();
const batcherFor = (locale: string) => {
  if (!batchers.has(locale)) batchers.set(locale, new TranslationBatcher(locale));
  return batchers.get(locale)!;
};

/** Shows the original immediately, then the persisted machine translation when ready (Section 11). */
function useFieldTranslation(entityKind: EntityKind, entityId: string, field: Field, sourceLocale: string, source: string, contentRevision?: number) {
  const locale = currentLocale();
  const wanted = !!source.trim() && needsTranslation(sourceLocale, locale);
  const query = useQuery({
    queryKey: ['translation', entityKind, entityId, field, locale, contentRevision ?? 0, source.length],
    queryFn: () => batcherFor(locale).load(entityKind, entityId, field),
    enabled: wanted,
    staleTime: 60_000,
    refetchInterval: (q) => {
      const status = q.state.data?.fields.find((f) => f.field === field)?.status;
      return status === 'PENDING' ? POLL_PENDING : false;
    },
  });
  const result = query.data?.fields.find((f) => f.field === field) ?? null;
  return { wanted, result, locale };
}

const CONTENT_LANGUAGES = ['es', 'pt-BR', 'en'] as const;

/**
 * Original-language correction (Section 11): editors fix a wrong detection; translations are keyed by
 * source language, so new ones are created for the corrected language and the text never changes.
 */
export function OriginalLanguage({ value, canEdit, onChange }: { value: string; canEdit: boolean; onChange: (locale: string) => void }) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const name = (code: string) => (code === 'und' ? '—' : languageName(code, locale));
  if (!canEdit) return <span>{name(value)}</span>;
  const options: string[] = (CONTENT_LANGUAGES as readonly string[]).includes(value) || value === 'und' ? [...CONTENT_LANGUAGES] : [value, ...CONTENT_LANGUAGES];
  return (
    <NativeSelect className="h-7" value={value === 'und' ? '' : value} onChange={(e) => e.target.value && onChange(e.target.value)} aria-label={t('languageCorrection.label')} title={t('languageCorrection.hint')}>
      {value === 'und' ? <option value="">—</option> : null}
      {options.map((code) => (
        <option key={code} value={code}>
          {name(code)}
        </option>
      ))}
    </NativeSelect>
  );
}

interface ReportTarget {
  entityKind: EntityKind;
  entityId: string;
  field: Field;
}

/** Reports an incorrect machine translation; optionally regenerates it. The original never changes. */
function ReportTranslationDialog({ target, onClose }: { target: ReportTarget; onClose: () => void }) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const queryClient = useQueryClient();
  const [comment, setComment] = useState('');
  const [regenerate, setRegenerate] = useState(true);
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent title={t('translationReport.title')} description={t('translationReport.hint')} closeLabel={t('common.close')}>
        <form
          className="flex flex-col gap-3"
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            try {
              await api.call('reportTranslation', { body: { ...target, targetLocale: locale as 'en', ...(comment.trim() ? { comment: comment.trim() } : {}), regenerate } });
              await queryClient.invalidateQueries({ queryKey: ['translation', target.entityKind, target.entityId] });
              toast.success(t('translationReport.thanks'));
              onClose();
            } catch (error) {
              toastError(error);
            } finally {
              setBusy(false);
            }
          }}
        >
          <FormField label={t('translationReport.comment')}>
            <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} maxLength={2000} autoFocus />
          </FormField>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={regenerate} onCheckedChange={(v) => setRegenerate(v === true)} /> {t('translationReport.regenerate')}
          </label>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="primary" loading={busy}>
              {t('translationReport.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function TranslationNote({ status, sourceLocale, showOriginal, onToggle, report }: { status: string; sourceLocale: string; showOriginal: boolean; onToggle: () => void; report?: ReportTarget }) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const [reporting, setReporting] = useState(false);
  if (status === 'PENDING') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-fg-subtle" role="status">
        <Loader2 className="size-3 animate-spin" /> {t('common.translating')}
      </span>
    );
  }
  if (status === 'FAILED') return <span className="text-xs text-fg-subtle">{t('common.translationFailed')}</span>;
  if (status !== 'READY') return null;
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5 text-xs text-fg-subtle">
      <Languages className="size-3" />
      {showOriginal ? null : t('common.machineTranslated', { language: languageName(sourceLocale, locale) })}
      <button type="button" className="font-medium text-accent hover:underline" onClick={onToggle}>
        {showOriginal ? t('common.viewTranslation') : t('common.viewOriginal')}
      </button>
      {report && !showOriginal ? (
        <button type="button" className="inline-flex items-center gap-0.5 hover:text-fg" onClick={() => setReporting(true)}>
          <Flag className="size-3" /> {t('translationReport.action')}
        </button>
      ) : null}
      {reporting && report ? <ReportTranslationDialog target={report} onClose={() => setReporting(false)} /> : null}
    </span>
  );
}

export function TranslatableMarkdown({
  entityKind,
  entityId,
  field,
  source,
  sourceLocale,
  contentRevision,
  className,
}: {
  entityKind: EntityKind;
  entityId: string;
  field: Field;
  source: string;
  sourceLocale: string;
  contentRevision?: number;
  className?: string;
}) {
  const { wanted, result } = useFieldTranslation(entityKind, entityId, field, sourceLocale, source, contentRevision);
  const [showOriginal, setShowOriginal] = useState(false);
  const translated = result?.status === 'READY' && result.text ? result.text : null;
  return (
    <div className={className}>
      <MarkdownView source={!showOriginal && translated ? translated : source} />
      {wanted && result ? (
        <div className="mt-1">
          <TranslationNote status={result.status} sourceLocale={result.sourceLocale} showOriginal={showOriginal} onToggle={() => setShowOriginal((v) => !v)} report={{ entityKind, entityId, field }} />
        </div>
      ) : null}
    </div>
  );
}

export function TranslatableText({ entityKind, entityId, field, source, sourceLocale, contentRevision, as: As = 'span', className }: { entityKind: EntityKind; entityId: string; field: Field; source: string; sourceLocale: string; contentRevision?: number; as?: 'span' | 'h1' | 'h2'; className?: string }) {
  const { wanted, result } = useFieldTranslation(entityKind, entityId, field, sourceLocale, source, contentRevision);
  const [showOriginal, setShowOriginal] = useState(false);
  const translated = result?.status === 'READY' && result.text ? result.text : null;
  const text = useMemo(() => (!showOriginal && translated ? translated : source), [showOriginal, translated, source]);
  useEffect(() => {
    setShowOriginal(false);
  }, [source]);
  return (
    <>
      <As className={className} lang={!showOriginal && translated ? currentLocale() : sourceLocale}>
        {text}
      </As>
      {wanted && result && result.status !== 'NOT_REQUIRED' ? (
        <div className="mt-0.5">
          <TranslationNote status={result.status} sourceLocale={result.sourceLocale} showOriginal={showOriginal} onToggle={() => setShowOriginal((v) => !v)} report={{ entityKind, entityId, field }} />
        </div>
      ) : null}
    </>
  );
}

/** Acceptance criteria translate as one id-to-text map so each criterion keeps its identity. */
export function useTranslatedCriteria(entityId: string, sourceLocale: string, criteria: ReadonlyArray<{ id: string; text: string }>, contentRevision?: number) {
  const source = criteria.map((c) => c.text).join('\n');
  const { wanted, result } = useFieldTranslation('WORK_ITEM', entityId, 'acceptanceCriteria', sourceLocale, source, contentRevision);
  const [showOriginal, setShowOriginal] = useState(false);
  const map = useMemo<Record<string, string>>(() => {
    if (showOriginal || result?.status !== 'READY' || !result.text) return {};
    try {
      return JSON.parse(result.text) as Record<string, string>;
    } catch {
      return {};
    }
  }, [result, showOriginal]);
  const note =
    wanted && result ? <TranslationNote status={result.status} sourceLocale={result.sourceLocale} showOriginal={showOriginal} onToggle={() => setShowOriginal((v) => !v)} report={{ entityKind: 'WORK_ITEM', entityId, field: 'acceptanceCriteria' }} /> : null;
  return { textOf: (c: { id: string; text: string }) => map[c.id] ?? c.text, note };
}
